import React, { useMemo } from 'react'
import { Form, Input as InputAntd } from 'antd'
import dp from 'dot-prop-immutable'
import { InputProps } from 'antd/lib/input'
import { FormItemProps } from 'antd/lib/form'

import { useT } from 'src/core/hooks/use-translation'

import mask from './mask'

type Props = InputProps &
  Pick<FormItemProps, 'label' | 'required'> & {
    dataTestId: string
    decimalSeparator?: string
    formikProps: any
    precision?: number
    thousandSeparator?: string
  }

function InputCurrency({
  dataTestId = 'input-currency',
  decimalSeparator = ',',
  disabled = false,
  formikProps,
  label,
  name,
  placeholder,
  precision = 2,
  required = true,
  size = 'large',
  thousandSeparator = '.'
}: Props): JSX.Element {
  const { t } = useT()

  const error = useMemo(() => {
    return dp.get(formikProps.errors, name)
  }, [formikProps.errors, name])

  const touched = useMemo(() => {
    return dp.get(formikProps.touched, name)
  }, [formikProps.touched, name])

  const maskedValue = useMemo(() => {
    const value = dp.get(formikProps.values, name)

    if (value > 0) {
      return mask({
        decimalSeparator,
        formatStr: true,
        precision,
        thousandSeparator,
        value
      }).maskedValue
    }

    return `0${decimalSeparator}${'0'.repeat(precision)}`
  }, [decimalSeparator, formikProps.values, name, precision, thousandSeparator])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const masked = mask({
      decimalSeparator,
      precision,
      thousandSeparator,
      value: e.target.value
    })

    formikProps.setFieldValue(name, masked.value)
  }

  return (
    <Form.Item
      colon={false}
      hasFeedback
      help={touched && typeof error === 'string' ? t(error) : ''}
      label={t(label)}
      required={required}
      validateStatus={
        error && touched ? 'error' : !error && touched ? 'success' : ''
      }
    >
      <InputAntd
        allowClear={!required}
        data-test-id={dataTestId}
        disabled={disabled}
        name={name}
        onBlur={formikProps.handleBlur}
        onChange={handleChange}
        placeholder={t(placeholder)}
        size={size}
        value={maskedValue}
      />
    </Form.Item>
  )
}

export default InputCurrency
